/**
 * Re-authentication before an action that is hard to take back.
 *
 * The three actions guarded by it — creating a school, granting a licence, granting
 * the platform role — all provision access rather than change a setting, and two of
 * them cannot be undone from this panel at all.
 *
 * It is not a permission check; the server already did that. It is a check that the
 * person at the keyboard is still the person who signed in, which is the failure
 * this panel is actually exposed to: a laptop left open, not a stolen token.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { ShieldCheck } from 'lucide-react'
import { api } from '../lib/api'
import { Field, FormError, GhostButton, PrimaryButton, TextInput } from './form'

/** Asks for the password and resolves true only if it verified. */
type StepUp = (action: string) => Promise<boolean>

const StepUpContext = createContext<StepUp | null>(null)

interface Pending {
  action: string
  resolve: (confirmed: boolean) => void
}

export function StepUpProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null)
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const pendingRef = useRef<Pending | null>(null)

  const request = useCallback<StepUp>((action) => {
    return new Promise<boolean>((resolve) => {
      const next = { action, resolve }
      pendingRef.current = next
      setPassword('')
      setError(null)
      setPending(next)
    })
  }, [])

  const settle = (confirmed: boolean) => {
    pendingRef.current?.resolve(confirmed)
    pendingRef.current = null
    setPending(null)
    setPassword('')
  }

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      // A login against the signed-in account's own email. The fresh session it
      // returns is harmless — the store is keyed the same way, so the effect is a
      // token refresh plus proof that the password was known.
      const me = await api.learner.profile()
      if (!me.email) throw new Error('This account has no email to verify against.')
      await api.auth.login(me.email, password)
      settle(true)
    } catch (caught) {
      // A wrong password is the expected case here, and it must leave the existing
      // session alone — a typo is not a reason to sign someone out.
      setError(
        caught instanceof Error && caught.message ? caught.message : 'That password did not match.',
      )
    } finally {
      setBusy(false)
    }
  }

  const value = useMemo(() => request, [request])

  return (
    <StepUpContext.Provider value={value}>
      {children}

      {pending && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm your identity"
        >
          <div className="card w-full max-w-md space-y-4 p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-accent-wash text-accent">
                <ShieldCheck size={20} />
              </div>
              <div>
                <h2 className="text-base font-semibold tracking-tight text-ink">
                  Confirm it&rsquo;s you
                </h2>
                <p className="text-sm text-muted">{pending.action}</p>
              </div>
            </div>

            <Field label="Password">
              <TextInput
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && password) void confirm()
                  if (e.key === 'Escape') settle(false)
                }}
              />
            </Field>

            <FormError message={error} />

            <div className="flex gap-2">
              <PrimaryButton onClick={() => void confirm()} disabled={busy || !password}>
                {busy ? 'Checking…' : 'Confirm'}
              </PrimaryButton>
              <GhostButton onClick={() => settle(false)}>Cancel</GhostButton>
            </div>
          </div>
        </div>
      )}
    </StepUpContext.Provider>
  )
}

export function useStepUp(): StepUp {
  const value = useContext(StepUpContext)
  if (!value) throw new Error('useStepUp must be used inside a StepUpProvider')
  return value
}
